import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  DollarSign,
  Check,
  X,
  Eye,
  Download,
  Loader2,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatsCard } from '@/components/shared/StatsCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  getPayoutList,
  getPayoutById,
  getPayoutCount,
  moderatePayout,
  getPayoutEvidence,
} from '@/services/payoutsApi';
import { getDropDownFirms } from '@/services/newsApi'; // or propFirmApi

const METHOD_COLORS = {
  'Bank Wire': 'bg-blue-100 text-blue-700',
  Crypto: 'bg-purple-100 text-purple-700',
  PayPal: 'bg-indigo-100 text-indigo-700',
  Deel: 'bg-emerald-100 text-emerald-700',
  Wise: 'bg-cyan-100 text-cyan-700',
  Rise: 'bg-orange-100 text-orange-700',
};

const parseAmount = (value) => {
  const n = Number(value);
  return isNaN(n) ? 0 : n;
};

const formatAmount = (value) => {
  const n = parseAmount(value);
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

export const VerifiedPayoutsPage = () => {
  const [payouts, setPayouts] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [firmFilter, setFirmFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');

  const [selectedPayout, setSelectedPayout] = useState(null);
  const [verifyPayout, setVerifyPayout] = useState(null);
  const [rejectPayout, setRejectPayout] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [proofUrl, setProofUrl] = useState('');

  const [pendingCount, setPendingCount] = useState(0);

  // ==================== FETCH ====================
  const fetchPayouts = async () => {
    try {
      setLoading(true);
      const body = {};
      if (firmFilter !== 'all') body.firm_id = firmFilter;
      if (methodFilter !== 'all') body.payment_method = methodFilter;

      const res = await getPayoutList(body);
      const list = res?.data || (Array.isArray(res) ? res : []);
      setPayouts(list);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load payouts');
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const res = await getPayoutCount('pending');
      setPendingCount(res?.count ?? 0);
    } catch {
      // fallback from list
    }
  };

  const fetchFirms = async () => {
    try {
      const data = await getDropDownFirms();
      setFirms(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchFirms();
    fetchPendingCount();
  }, []);

  useEffect(() => {
    fetchPayouts();
  }, [firmFilter, methodFilter]);

  // ==================== FILTER ====================
  const filteredPayouts = useMemo(() => {
    return payouts.filter((p) => {
      const matchesTab = activeTab === 'all' || p.status === activeTab;
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !q ||
        p.username?.toLowerCase().includes(q) ||
        p.firm_name?.toLowerCase().includes(q);
      return matchesTab && matchesSearch;
    });
  }, [payouts, activeTab, searchTerm]);

  const verifiedThisMonth = useMemo(() => {
    const thisMonth = new Date().getMonth();
    const thisYear = new Date().getFullYear();
    return payouts.filter((p) => {
      if (p.status !== 'verified' || !p.created_at) return false;
      const d = new Date(p.created_at);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    }).length;
  }, [payouts]);

  const totalVerified = useMemo(
    () => payouts.filter((p) => p.status === 'verified').length,
    [payouts]
  );

  const displayPending =
    pendingCount || payouts.filter((p) => p.status === 'pending').length;

  // ==================== ACTIONS ====================
  const handleModerate = async (id, status, note = '') => {
    try {
      await moderatePayout({ id, status, moderator_note: note });
      toast.success(status === 'verified' ? 'Payout verified' : 'Payout rejected');
      setVerifyPayout(null);
      setRejectPayout(null);
      setRejectReason('');
      setSelectedPayout(null);
      fetchPayouts();
      fetchPendingCount();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Action failed');
    }
  };

  const handleVerify = (payout) => {
    handleModerate(payout.id, 'verified');
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    handleModerate(rejectPayout.id, 'rejected', rejectReason.trim());
  };

  const openDetail = async (payout) => {
    setSelectedPayout(payout);
    setProofUrl('');
    try {
      setDetailLoading(true);
      const res = await getPayoutById(payout.id);
      const data = res?.data || res;
      if (data) {
        setSelectedPayout(data);
        if (data.proof_filename) {
          try {
            const evidence = await getPayoutEvidence(data.proof_filename);
            // API may return url string, { url }, or blob base64
            const url =
              evidence?.data?.url ||
              evidence?.url ||
              evidence?.data ||
              (typeof evidence === 'string' ? evidence : '');
            if (url && typeof url === 'string') setProofUrl(url);
          } catch {
            // ignore evidence errors
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const methodBadgeClass = (method) =>
    METHOD_COLORS[method] || 'bg-slate-100 text-slate-700';

  return (
    <div>
      <PageHeader
        title="Verified Payouts"
        description="Manage and verify trader payout submissions"
        breadcrumbs={['Admin', 'Community', 'Verified Payouts']}
      />

      <div className="grid gap-6 md:grid-cols-3 mb-6">
        <StatsCard
          title="Pending"
          value={displayPending}
          icon={DollarSign}
          iconBg="bg-amber-100 text-amber-600"
        />
        <StatsCard
          title="Verified This Month"
          value={verifiedThisMonth}
          icon={Check}
          iconBg="bg-emerald-100 text-emerald-600"
        />
        <StatsCard
          title="Total Verified"
          value={totalVerified}
          icon={DollarSign}
          iconBg="bg-blue-100 text-blue-600"
        />
      </div>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="pending">
                  Pending
                  {displayPending > 0 && (
                    <Badge
                      variant="destructive"
                      className="ml-2 h-5 px-1.5 text-xs bg-amber-500"
                    >
                      {displayPending}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="verified">Verified</TabsTrigger>
                <TabsTrigger value="rejected">Rejected</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex flex-wrap items-center gap-3">
              <Select value={firmFilter} onValueChange={setFirmFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by firm" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Firms</SelectItem>
                  {firms.map((firm) => (
                    <SelectItem key={firm.id} value={firm.id}>
                      {firm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={methodFilter} onValueChange={setMethodFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Methods</SelectItem>
                  {Object.keys(METHOD_COLORS).map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Trader
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Firm
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">
                      Amount
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Method
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Date
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Status
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayouts.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-12 text-slate-500"
                      >
                        No payouts found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPayouts.map((payout) => (
                      <TableRow key={payout.id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback>
                                {payout.username?.[0]?.toUpperCase() || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm font-medium text-slate-900">
                              {payout.username}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {payout.firm_logo && (
                              <img
                                src={payout.firm_logo}
                                alt=""
                                className="h-6 w-6 rounded"
                              />
                            )}
                            <span className="text-sm text-slate-700">
                              {payout.firm_name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm font-bold text-slate-900">
                            {formatAmount(payout.amount)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={methodBadgeClass(payout.payment_method)}>
                            {payout.payment_method || '—'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600">
                            {payout.created_at
                              ? format(new Date(payout.created_at), 'MMM dd, yyyy')
                              : '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={payout.status} />
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {payout.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => setVerifyPayout(payout)}
                                  className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700"
                                >
                                  <Check className="h-3 w-3 mr-1" />
                                  Verify
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setRejectPayout(payout)}
                                  className="h-7 px-2"
                                >
                                  <X className="h-3 w-3 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDetail(payout)}
                              className="h-7 px-2"
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet
        open={!!selectedPayout}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedPayout(null);
            setProofUrl('');
          }
        }}
      >
        <SheetContent className="w-[480px] overflow-y-auto">
          {selectedPayout && (
            <>
              <SheetHeader>
                <SheetTitle>Payout Details</SheetTitle>
              </SheetHeader>

              {detailLoading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="mt-6 space-y-6">
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">TRADER</p>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback>
                          {selectedPayout.username?.[0]?.toUpperCase() || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-slate-900">
                          {selectedPayout.username}
                        </p>
                        <StatusBadge status={selectedPayout.status} />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <p className="text-xs text-muted-foreground mb-2">FIRM</p>
                    <div className="flex items-center gap-2">
                      {selectedPayout.firm_logo && (
                        <img
                          src={selectedPayout.firm_logo}
                          alt=""
                          className="h-8 w-8 rounded"
                        />
                      )}
                      <span className="font-medium">{selectedPayout.firm_name}</span>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-3">
                      PAYOUT DETAILS
                    </p>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Amount</span>
                        <span className="text-lg font-bold text-slate-900">
                          {formatAmount(selectedPayout.amount)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Method</span>
                        <Badge
                          className={methodBadgeClass(selectedPayout.payment_method)}
                        >
                          {selectedPayout.payment_method || '—'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Submitted</span>
                        <span className="text-sm font-medium">
                          {selectedPayout.created_at
                            ? format(
                                new Date(selectedPayout.created_at),
                                'MMMM dd, yyyy'
                              )
                            : '—'}
                        </span>
                      </div>
                      {selectedPayout.verified_at && (
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Verified at</span>
                          <span className="text-sm font-medium">
                            {format(
                              new Date(selectedPayout.verified_at),
                              'MMMM dd, yyyy'
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {(proofUrl || selectedPayout.proof_filename) && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">
                        PROOF DOCUMENT
                      </p>
                      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                        {proofUrl ? (
                          <img
                            src={proofUrl}
                            alt="Proof"
                            className="w-full rounded"
                          />
                        ) : (
                          <p className="text-sm text-slate-600">
                            {selectedPayout.proof_filename}
                          </p>
                        )}
                        {proofUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full mt-3"
                            onClick={() => window.open(proofUrl, '_blank')}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Open Proof
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedPayout.moderator_note && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">
                        MODERATOR NOTE
                      </p>
                      <p className="text-sm text-slate-700">
                        {selectedPayout.moderator_note}
                      </p>
                    </div>
                  )}

                  <Separator />

                  {selectedPayout.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setVerifyPayout(selectedPayout)}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Verify
                      </Button>
                      <Button
                        onClick={() => setRejectPayout(selectedPayout)}
                        variant="outline"
                        className="flex-1"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  )}

                  {selectedPayout.status === 'verified' && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                      <Check className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-emerald-900">
                        Verified and visible on public page
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!verifyPayout}
        onOpenChange={() => setVerifyPayout(null)}
        title="Verify Payout"
        description={`Confirm verification of ${formatAmount(
          verifyPayout?.amount
        )} payout from ${verifyPayout?.firm_name || 'this firm'}? This will make it publicly visible.`}
        confirmLabel="Verify Payout"
        onConfirm={() => handleVerify(verifyPayout)}
      />

      <ConfirmDialog
        open={!!rejectPayout}
        onOpenChange={() => {
          setRejectPayout(null);
          setRejectReason('');
        }}
        title="Reject Payout"
        description={
          <div className="space-y-3">
            <p>Why are you rejecting this payout?</p>
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
            />
          </div>
        }
        confirmLabel="Reject Payout"
        variant="destructive"
        onConfirm={handleReject}
      />
    </div>
  );
};